import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { PlatformService } from '../universal/platform.service';
import { BREAKPOINTS } from './breakpoint-config';

export const SSR_BREAKPOINT_ALIAS = 'xs';
const SSR_BREAKPOINT_SECONDARY_ALIASES = ['lt-sm', 'lt-md', 'lt-lg', 'lt-xl', 'xs-liquid', 'lt-xs-fixed'];

export interface Breakpoint {
    alias: string;
    query: string;
    matches: boolean;
    isMain: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class BreakpointService {
    activeBreakpoint$: Observable<string> = of(SSR_BREAKPOINT_ALIAS);
    allActiveQueries$: Observable<Breakpoint[]> = of([]);

    private allQueries: Breakpoint[] = [];
    private allMatchMediaChanges$ = new BehaviorSubject<Breakpoint>(null);
    private useSafariAddListener = false;

    constructor(
        private ngZone: NgZone,
        private platform: PlatformService
    ) {}

    init() {
        if (this.platform.isServer) {
            this.setupSsr();
            return;
        }

        // Safari < 14 only support the now deprecated 'addListener' method on MediaQueryList
        if (typeof window.matchMedia('').addEventListener === 'undefined') {
            this.useSafariAddListener = true;
        }

        // initialize with currently active media queries
        for (const [alias, [query, isMainBreakpoint]] of Object.entries(BREAKPOINTS)) {
            const isCurrentlyMatching = window.matchMedia(query).matches;
            const breakpoint = { alias, query, matches: isCurrentlyMatching, isMain: isMainBreakpoint } as Breakpoint;

            this.allQueries.push(breakpoint);

            // setup listener for future changes
            this.setupMatchMediaListener(query);

            // emit initial breakpoint state if active
            if (isCurrentlyMatching) {
                this.allMatchMediaChanges$.next(breakpoint);
            }
        }

        // setup observables for consumers
        this.allActiveQueries$ = this.allMatchMediaChanges$.pipe(
            debounceTime(0),
            switchMap(() => of(this.allQueries.filter(bp => bp.matches))),
            shareReplay(1)
        );

        // One of the main, mutually exclusive breakpoints (e.g. xs, sm, md, lg, xl)
        this.activeBreakpoint$ = this.allActiveQueries$.pipe(
            map(allActiveQueries => allActiveQueries.find(breakpoint => breakpoint.isMain)?.alias || 'all'),
            distinctUntilChanged()
        );
    }

    isActive(alias: string): boolean {
        return this.allQueries.some(bp => bp.alias === alias && bp.matches);
    }

    isActive$(alias: string): Observable<boolean> {
        return this.allActiveQueries$.pipe(
            map(allActiveQueries => allActiveQueries.some(breakpoint => breakpoint.alias === alias)),
            distinctUntilChanged()
        );
    }

    private setupSsr() {
        // Set all aliases for 'xs' to active (as we do not know what the client size is when doing SSR, we assume it is 'xs')
        for (const [alias, [query, isMainBreakpoint]] of Object.entries(BREAKPOINTS)) {
            const isCurrentlyMatching = SSR_BREAKPOINT_SECONDARY_ALIASES.includes(alias);
            const breakpoint = { alias, query, matches: isCurrentlyMatching, isMain: isMainBreakpoint } as Breakpoint;
            this.allQueries.push(breakpoint);
            this.allActiveQueries$ = of(this.allQueries.filter(bp => bp.matches));
        }
    }

    private setupMatchMediaListener(query) {
        // subscribe to events for all given media queries
        const listenerFn = (mqlEvent: MediaQueryListEvent) => {
            const changedBreakpoint = this.allQueries.find(breakpoint => breakpoint.query === query);
            changedBreakpoint.matches = mqlEvent.matches;

            this.ngZone.run(() => this.allMatchMediaChanges$.next(changedBreakpoint));
        };

        if (this.useSafariAddListener) {
            window.matchMedia(query).addListener(listenerFn);
        } else {
            window.matchMedia(query).addEventListener('change', listenerFn);
        }
    }
}
